import axios from 'axios'
import { basePath } from '../api'

/**
 * Response from POST /roster/import
 */
export interface RosterImportResponse {
  rosterHash: string
  status: 'COMPLETED' | 'ALREADY_COMPLETED' | 'FAILED'
  message: string
  statusEndpoint: string
  startedAt: string
  completedAt: string
  errorMessage: string | null
}

/**
 * Response from GET /roster/import/status/{rosterHash}
 */
export interface RosterImportStatus {
  rosterHash: string
  status: 'STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED'
  currentPhase: number
  phaseName: 'STARTED' | 'CREATE_UPDATE' | 'ASSIGN_TEAMS' | 'CLEANUP' | 'COMPLETE'
  totalEntries: number
  processedCount: number
  percentComplete: number
  startTime: string
  lastCheckpointTime: string
  errorMessage: string | null
}

export class RosterService {
  /**
   * Upload a roster file for import
   * Server computes SHA256 hash and returns it in response
   * @param file - The CSV file to import
   * @returns Promise<RosterImportResponse> - Upload response with hash and status
   */
  static async importRoster(file: File): Promise<RosterImportResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await axios.post(`${basePath}/roster/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data as RosterImportResponse
  }

  /**
   * Get the import status for a roster file
   * @param rosterHash - SHA256 hash from the import response
   * @returns Promise<RosterImportStatus> - Current import status with progress details
   */
  static async getImportStatus(rosterHash: string): Promise<RosterImportStatus> {
    try {
      const response = await axios.get(`${basePath}/roster/import/status/${rosterHash}`)
      return response.data as RosterImportStatus
    } catch (error) {
      throw new Error(`Failed to get roster import status: ${error}`)
    }
  }

  /**
   * Poll for roster import status with configurable interval
   * Automatically stops when import completes or fails
   * @param rosterHash - SHA256 hash from the import response
   * @param onStatusUpdate - Optional callback for each status update
   * @param maxAttempts - Maximum polling attempts (0 = unlimited, default: 0)
   * @param pollInterval - Polling interval in milliseconds (default: 3000)
   * @returns Promise<RosterImportStatus> - Final status when COMPLETED or FAILED
   */
  static async pollImportStatus(
    rosterHash: string,
    onStatusUpdate?: (status: RosterImportStatus) => void,
    maxAttempts = 0,
    pollInterval = 3000
  ): Promise<RosterImportStatus> {
    let attempts = 0

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await this.getImportStatus(rosterHash)

          if (onStatusUpdate) {
            onStatusUpdate(status)
          }

          // Check if import is complete
          if (status.status === 'COMPLETED' || status.status === 'FAILED') {
            resolve(status)
            return
          }

          // Check if max attempts reached
          if (maxAttempts > 0 && attempts >= maxAttempts) {
            reject(new Error('Max polling attempts reached'))
            return
          }

          attempts++
          setTimeout(poll, pollInterval)
        } catch (error) {
          reject(error)
        }
      }

      poll()
    })
  }
}
